import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, ArrowRight, Loader } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const res = await axios.get(`${API}/payments/status/${sid}`, { withCredentials: true });
      if (res.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (res.data.status === "expired") {
        setStatus("failed");
        return;
      }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch {
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  const handleBack = () => {
    if (user?.role === "business") navigate("/business");
    else navigate("/customer");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50/30 to-rose-50/20">
      <Navbar />
      <div className="pt-32 pb-20 px-6 lg:px-16">
        <div className="max-w-lg mx-auto text-center">
          {status === "processing" && (
            <div className="animate-fade-up" data-testid="payment-processing">
              <Loader className="h-16 w-16 text-orange-500 mx-auto mb-8 animate-spin" />
              <h1 className="font-heading text-4xl font-black tracking-tight text-slate-800 mb-4">
                {t("pay.processing")}
              </h1>
            </div>
          )}

          {status === "success" && (
            <div className="animate-fade-up" data-testid="payment-success">
              <div className="w-20 h-20 bg-emerald-50 border-2 border-emerald-400 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h1 className="font-heading text-4xl font-black tracking-tight text-slate-800 mb-4">
                {t("pay.success")}
              </h1>
              <p className="text-sm text-slate-500 mb-12">{t("pay.success.desc")}</p>
              <Button
                data-testid="back-to-dashboard-button"
                onClick={handleBack}
                className="rounded-xl px-8 py-6 text-base bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-lg hover:shadow-emerald-200 border-0 transition-all duration-200"
              >
                {t("pay.back")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {(status === "failed" || status === "timeout") && (
            <div className="animate-fade-up" data-testid="payment-failed">
              <div className="w-20 h-20 bg-red-50 border-2 border-red-400 rounded-full flex items-center justify-center mx-auto mb-8">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h1 className="font-heading text-4xl font-black tracking-tight text-slate-800 mb-4">
                {t("pay.failed")}
              </h1>
              <Button
                data-testid="retry-button"
                onClick={handleBack}
                className="rounded-xl px-8 py-6 text-base bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg hover:shadow-orange-200 border-0 transition-all duration-200"
              >
                {t("pay.back")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
